import { initGrowthBook } from '@flysas-tech/wfd-growthbook-js';
import { getLocale } from 'i18n';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { MaintenanceModeKey } from 'shared';

type GrowthbookProviderValue = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gb: Record<string, any>;
  inMaintenanceMode: boolean;
};

const getMarket = () => getLocale().split('-')[0].toUpperCase();

export const GrowthbookContext = createContext<GrowthbookProviderValue | undefined>(undefined);

export const GrowthbookProvider = ({
  environment,
  market,
  maintenanceModeKey,
  children,
  attributes,
}: {
  environment?: string;
  market?: string;
  maintenanceModeKey: MaintenanceModeKey;
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: Record<string, any>;
}) => {
  const [inMaintenanceMode, setInMaintenaceMode] = useState<boolean | undefined>();

  const gbClient = useMemo(() => {
    if (!environment) return;

    const { gb } = initGrowthBook({
      env: environment === 'production' ? 'prod' : 'test',
      clientKey: environment === 'production' ? 'sdk-cmXuu0wpBgiLZCw3' : 'sdk-aMOEJccuBF2aMpMF',
    });

    gb.setAttributes({
      ...gb.getAttributes(),
      market: market ?? getMarket(),
      ...(attributes && { ...attributes }),
    });

    return gb;
  }, [environment]);

  useEffect(() => {
    if (!gbClient) return;

    const loadFeaturesAndCheckFlow = async () => {
      await gbClient.init();
      setInMaintenaceMode(gbClient.isOn(maintenanceModeKey));
    };

    loadFeaturesAndCheckFlow();
  }, [gbClient]);

  const contextValue = useMemo(() => {
    if (!gbClient || inMaintenanceMode === undefined) return undefined;

    return {
      gb: gbClient,
      inMaintenanceMode: inMaintenanceMode,
    };
  }, [gbClient, inMaintenanceMode]);

  if (!contextValue) {
    return null;
  }

  return <GrowthbookContext.Provider value={contextValue}>{children}</GrowthbookContext.Provider>;
};

export const useGrowthbook = (): GrowthbookProviderValue => {
  const context = useContext(GrowthbookContext);

  if (!context) {
    throw new Error('useGrowthbook must be used within a GrowthbookContext');
  }

  return context;
};
